/**
 * tutorStudentConversationViewModel — pure `(reduxState, input) → props` de la conversation du tuteur
 * avec un élève (l'onglet Messages de la fiche, `<TutorConversationPage>`), un sélecteur par
 * conversation montée (`makeTutorStudentConversationViewModel`).
 *
 * Le pendant, côté tuteur, de `studentTutorConversationViewModel` (patron A, boîte d'envoi locale),
 * sur les lectures de la branche tuteur de `ChatScreen.js` et `CommonChat.js` :
 *  - le fil vit dans le compte du TUTEUR (`currentUser.messages[threadId]`), ses messages à droite ;
 *  - le fil se trouve par les séances du tuteur, sinon par l'élève (`selectTutorStudentThreadId`, lu par
 *    l'onglet et passé ici : un seul calcul) ;
 *  - la pastille d'une pièce devenue activité, et l'activité qu'on ouvre en la touchant, se lisent
 *    sur les activités de l'ÉLÈVE (`currentUser` de `CommonChat` est l'élève, côté tuteur).
 * Le fil se groupe comme celui de l'élève (`conversationThread`).
 */
import { createSelector } from "reselect"

import {
    tutorStudentThreadId,
    type ApiTutorConversationAccount,
    type ApiTutorConversationStudent,
} from "../../../adapters/tutor/Students/tutorStudentConversationAdapter"
import { chatActivities } from "../../../adapters/student/Messaging/studentConversationAdapter"
import { type UIConversationDay, type UIConversationMessage } from "../../../ui/types/studentConversation"
import { selectAccount, selectDisplayedStudent } from "../../shared/displayedStudent"
import {
    CONVERSATION_NOT_FOUND,
    conversationErrorText,
    conversationThread,
    type ConversationOutboxEntry,
} from "../../student/Messaging/studentTutorConversationViewModel"

export type TutorStudentConversationInput = {
    studentId: string | number | null
    /** Le fil (`selectTutorStudentThreadId`) ; `null` : aucun connu. */
    threadId: string | null
    /** La base des chemins de pièces (`API_URL`). */
    apiUrl: string
    outbox: ConversationOutboxEntry[]
    /** La lecture du fil a échoué. */
    error: unknown
    hasEarlier: boolean
    voiceMessageId: string | null
    voiceLabel: string
}

export type TutorStudentConversationViewModel = {
    threadId: string | null
    days: UIConversationDay[]
    /** Le fil à plat, du plus ancien au plus récent. */
    messages: UIConversationMessage[]
    /** L'activité née d'un message, par id de message. */
    activityIdByMessage: Map<string, string>
    loading: boolean
    error: string | null
    hasEarlier: boolean
}

/** Ce que rend une conversation cachée. */
export const INACTIVE_TUTOR_CONVERSATION: TutorStudentConversationViewModel = {
    threadId: null,
    days: [],
    messages: [],
    activityIdByMessage: new Map(),
    loading: false,
    error: null,
    hasEarlier: false,
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any
type In = TutorStudentConversationInput

const studentOf = (state: ReduxState, studentId: string | number | null) =>
    studentId == null ? null : (selectDisplayedStudent(state, studentId) as ApiTutorConversationStudent | null)

/** Le fil du tuteur avec cet élève : un id, que l'onglet lit (il ne redessine que s'il change). */
export function selectTutorStudentThreadId(state: ReduxState, studentId: string | number | null): string | null {
    if (studentId == null) return null
    return tutorStudentThreadId(
        selectAccount(state) as ApiTutorConversationAccount | null,
        studentOf(state, studentId),
        studentId
    )
}

export const makeTutorStudentConversationViewModel = () =>
    createSelector(
        [
            // Le fil lu et l'id du compte, pas le compte : il change à chaque relecture d'un élève.
            (state: ReduxState, input: In) =>
                input.threadId != null
                    ? (selectAccount(state) as ApiTutorConversationAccount | null)?.messages?.[input.threadId]
                    : undefined,
            (state: ReduxState) => (selectAccount(state) as ApiTutorConversationAccount | null)?.id,
            (state: ReduxState, input: In) => studentOf(state, input.studentId),
            (_state: ReduxState, input: In) => input.threadId,
            (_state: ReduxState, input: In) => input.apiUrl,
            (_state: ReduxState, input: In) => input.outbox,
            (_state: ReduxState, input: In) => input.error,
            (_state: ReduxState, input: In) => input.hasEarlier,
            (_state: ReduxState, input: In) => input.voiceMessageId,
            (_state: ReduxState, input: In) => input.voiceLabel,
        ],
        (
            raw,
            selfId,
            student,
            threadId,
            apiUrl,
            outbox,
            error,
            hasEarlier,
            voiceMessageId,
            voiceLabel
        ): TutorStudentConversationViewModel => {
            const activities = chatActivities(student)
            const { messages, days } = conversationThread({
                raw,
                outbox,
                selfId,
                apiUrl,
                activityStatusOf: activities.statusOf,
                voiceMessageId,
                voiceLabel,
            })
            // Sans fil connu, un élève pas encore lu (`getStudent` à l'ouverture) attend : son fil peut
            // venir de lui ; un élève lu sans fil, c'est la conversation introuvable.
            const failed =
                threadId == null
                    ? student == null
                        ? null
                        : CONVERSATION_NOT_FOUND
                    : error != null && raw == null
                      ? conversationErrorText(error)
                      : null
            return {
                threadId,
                days,
                messages,
                activityIdByMessage: activities.activityIdByMessage,
                loading: failed == null && raw == null,
                error: failed,
                hasEarlier: failed == null && hasEarlier,
            }
        }
    )
